import React, { CSSProperties } from "react";
import styled, { css } from "styled-components";
import Styles from "features/configuration/common/styles";
import useMedia from "utils/hooks/useMedia";
import {
  MARKETPLACE_PAYMENTS_FEATURE_FLAG,
  MOBILE_SEARCH,
} from "common/constants";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import Button from "common/Buttons/Button";
import { GroupData, LanguageNameSpaces } from "@shared/types";
import { useGroupSubscriptionData } from "features/settings/common/hooks";
import { typography } from "@shared/common/Themes/typography";
import { useUser } from "@shared/utils/hooks/misc";
import { getGroupSubscriptionExpiration } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface UserGroupSummaryProps {
  groupData: GroupData;
  style?: CSSProperties;
}

const UserGroupSummary = ({ groupData, style }: UserGroupSummaryProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { user } = useUser();
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { actionButton } = useGroupSubscriptionData(groupData);
  const hasFeatureFlag = user?.hasFeatureFlag(
    MARKETPLACE_PAYMENTS_FEATURE_FLAG
  );

  const getButton = () => {
    if (actionButton && hasFeatureFlag) {
      return (
        <Button
          type="primary"
          style={{ width: "100%", marginTop: 12 }}
          onClick={actionButton.onClick}
        >
          {actionButton.text}
        </Button>
      );
    }
  };

  return (
    <Wrapper style={style}>
      <Flex>
        <Styles.GroupImage src={groupData.image} $size={isMobile ? 40 : 50} />
        <Text
          as="div"
          style={{ flex: "auto", fontSize: 16 }}
          $weight={typography.fontWeights.MEDIUM}
        >
          {groupData.name}
        </Text>
        <div>
          {groupData.next_auto_renewal &&
            groupData.recurring_price &&
            hasFeatureFlag && (
              <Flex $justifyContent="flex-end">
                <Text
                  $weight={typography.fontWeights.MEDIUM}
                  $size={typography.fontSizes.L}
                >
                  ${groupData.recurring_price}
                </Text>
                <Text
                  type="secondary"
                  $weight={typography.fontWeights.MEDIUM}
                  style={{ marginLeft: 4 }}
                >
                  {t("duration_radio.per_month")}
                </Text>
              </Flex>
            )}
          {groupData.expiration_timestamp && (
            <Text type="secondary" $weight={typography.fontWeights.MEDIUM}>
              {getGroupSubscriptionExpiration(groupData, t)}
            </Text>
          )}
        </div>
      </Flex>
      {getButton()}
    </Wrapper>
  );
};

export default UserGroupSummary;

export const Wrapper = styled.div`
  background-color: ${(p) => p.theme.colors.titleCardBgColor};
  border-radius: 8px;
  padding: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border: ${p.theme.borders.panel};
      border-radius: ${p.theme.radii.card}px;
      box-shadow: ${p.theme.shadows.panel};
      padding: 20px 24px;
    `}
`;
